import React from 'react';
import axios from 'axios';
import Footer from './Footer';
import Header from './Header';

type LegalPageKind = 'terms' | 'privacy' | 'contact';
type AppPage = 'analysis' | 'mypage' | 'admin';

type Props = {
  kind: LegalPageKind;
  onLogoClick?: () => void;
  onNavigate?: (page: AppPage) => void;
  isAdmin?: boolean;
};

type Section = {
  title: string;
  body: string[];
};

const updatedAt = '2026年5月24日';

const pageContent: Record<LegalPageKind, { title: string; lead: string; sections: Section[] }> = {
  terms: {
    title: '利用規約',
    lead: 'NeedRadarをご利用いただく際の基本的なルールをまとめています。',
    sections: [
      {
        title: '第1条 適用',
        body: [
          '本規約は、NeedRadarが提供するキーワード分析、サジェスト取得、検索需要分析、AI分析その他関連機能の利用条件を定めるものです。',
          'ユーザーは、本サービスを利用することで本規約に同意したものとみなされます。',
        ],
      },
      {
        title: '第2条 アカウント',
        body: [
          'ユーザーは、Googleログインまたはメールリンク認証により本サービスを利用できます。',
          'アカウント情報の管理はユーザー自身の責任で行うものとします。',
        ],
      },
      {
        title: '第3条 利用枠とプラン',
        body: [
          '本サービスでは、無料枠、基本プラン、プレミアムプランなどの利用枠を設ける場合があります。',
          '検索ボリューム取得、関連キーワード取得、AI分析、ペルソナ生成、画像生成など、外部APIやAI処理を伴う機能には利用回数の制限を設ける場合があります。',
          'プラン内容、料金、利用枠は、事前の告知なく変更される場合があります。',
        ],
      },
      {
        title: '第4条 禁止事項',
        body: [
          '法令または公序良俗に反する行為、第三者の権利を侵害する行為、不正アクセス、過度なリクエスト、自動化された大量利用、サービス運営を妨害する行為を禁止します。',
          '取得したデータやAI生成結果を利用する際は、ユーザー自身の責任で内容を確認してください。',
        ],
      },
      {
        title: '第5条 外部サービスとAI生成結果',
        body: [
          '本サービスは、検索エンジン、外部API、AIサービスなどの情報をもとに分析結果を表示する場合があります。',
          '外部サービスの仕様変更、障害、制限により、機能の一部または全部が利用できなくなる場合があります。',
          'AIによる要約、ペルソナ、画像などの生成結果の正確性、完全性、有用性を保証するものではありません。',
        ],
      },
      {
        title: '第6条 免責',
        body: [
          '本サービスの利用により生じた損害、機会損失、データの誤り、外部サービスの障害等について、運営者は法令上認められる範囲で責任を負いません。',
          '本サービスは、予告なく変更、停止、終了する場合があります。',
        ],
      },
      {
        title: '第7条 規約の変更',
        body: [
          '運営者は、必要に応じて本規約を変更できるものとします。',
          '変更後の規約は、本サービス上に掲載された時点から効力を生じます。',
        ],
      },
    ],
  },
  privacy: {
    title: 'プライバシーポリシー',
    lead: 'NeedRadarにおけるユーザー情報と分析データの取り扱いについてまとめています。',
    sections: [
      {
        title: '1. 取得する情報',
        body: [
          '本サービスでは、ログイン時に取得されるユーザーID、メールアドレス、表示名、プロフィール画像などのアカウント情報を取得する場合があります。',
          'また、検索キーワード、利用履歴、お気に入り、利用回数、プラン情報、AI分析結果、画像生成結果など、サービス利用に伴う情報を保存する場合があります。',
        ],
      },
      {
        title: '2. 利用目的',
        body: [
          '取得した情報は、ログイン状態の管理、検索履歴やお気に入りの表示、利用制限の管理、キャッシュ表示、問い合わせ対応、サービス改善、不正利用防止のために利用します。',
          '外部APIやAIサービスを利用する機能では、分析に必要な範囲でキーワードや関連情報を外部サービスへ送信する場合があります。',
        ],
      },
      {
        title: '3. 利用する外部サービス',
        body: [
          '本サービスでは、Firebase Authentication、Cloud Firestore、Cloud Storage、Google Cloud Run、DataForSEO、OpenAI、Gemini API等を利用する場合があります。',
          '外部サービスの利用にあたっては、各サービス提供者の規約およびプライバシーポリシーが適用される場合があります。',
        ],
      },
      {
        title: '4. 第三者提供',
        body: [
          '法令に基づく場合を除き、ユーザーの同意なく個人情報を第三者に提供することはありません。',
          'ただし、サービス提供に必要な範囲で、クラウドサービス、認証サービス、分析API、AI API等にデータが送信・保存される場合があります。',
        ],
      },
      {
        title: '5. データの保存と削除',
        body: [
          'ユーザーの検索履歴、保存キーワード、キャッシュデータ、利用状況は、サービス提供に必要な期間保存される場合があります。',
          '削除や確認の希望がある場合は、お問い合わせページよりご連絡ください。',
        ],
      },
      {
        title: '6. セキュリティ',
        body: [
          '取得した情報について、不正アクセス、紛失、改ざん、漏えいを防ぐため、合理的な安全管理措置を講じます。',
        ],
      },
      {
        title: '7. 改定',
        body: [
          '本ポリシーは、必要に応じて変更されることがあります。',
          '重要な変更がある場合は、本サービス上で告知します。',
        ],
      },
    ],
  },
  contact: {
    title: 'お問い合わせ',
    lead: '不具合報告、有料プランのご相談、機能要望などはこちらからご連絡ください。',
    sections: [
      {
        title: 'ご連絡いただきたい内容',
        body: [
          '不具合の場合は、発生した画面、入力したキーワード、利用していた機能、エラーメッセージの有無を添えていただくと確認がスムーズです。',
          '有料プランや利用枠についてのご相談も、こちらから受け付けています。',
        ],
      },
    ],
  },
};

const contactTypeOptions = [
  { value: 'bug', label: '不具合' },
  { value: 'paid-plan', label: '有料プラン相談' },
  { value: 'feature-request', label: '機能要望' },
  { value: 'other', label: 'その他' },
];

const API_ORIGIN =
  process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';

const ContactForm: React.FC = () => {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [type, setType] = React.useState('paid-plan');
  const [message, setMessage] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setNotice(null);
    setError(null);

    try {
      await axios.post(`${API_ORIGIN}/api/contact-messages`, {
        name,
        email,
        type,
        message,
      });
      setNotice('お問い合わせを送信しました。確認してご連絡します。');
      setName('');
      setEmail('');
      setType('paid-plan');
      setMessage('');
    } catch (submitError) {
      const messageText = axios.isAxiosError(submitError)
        ? submitError.response?.data?.message || submitError.response?.data?.error || '送信できませんでした。'
        : '送信できませんでした。';
      setError(messageText);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mt-8 rounded-lg border border-blue-100 bg-blue-50/40 p-5">
      <h2 className="text-lg font-bold text-gray-900">お問い合わせフォーム</h2>
      <p className="mt-2 text-sm leading-6 text-gray-600">
        送信内容は管理画面に保存されます。返信が必要な場合は、連絡可能なメールアドレスを入力してください。
      </p>

      <form onSubmit={handleSubmit} className="mt-5 space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-semibold text-gray-700">お名前</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="任意"
              className="mt-2 w-full rounded border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700">メールアドレス</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              required
              className="mt-2 w-full rounded border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
            />
          </label>
        </div>

        <label className="block">
          <span className="text-sm font-semibold text-gray-700">種別</span>
          <select
            value={type}
            onChange={(event) => setType(event.target.value)}
            className="mt-2 w-full rounded border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
          >
            {contactTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-sm font-semibold text-gray-700">お問い合わせ内容</span>
          <textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="内容を入力してください"
            required
            rows={6}
            className="mt-2 w-full rounded border border-gray-200 bg-white px-3 py-2 text-sm leading-6 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
          />
        </label>

        {notice && (
          <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
            {notice}
          </div>
        )}
        {error && (
          <div className="rounded border border-red-100 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !email.trim() || !message.trim()}
          className="rounded bg-blue-500 px-5 py-3 text-sm font-bold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? '送信中...' : '送信する'}
        </button>
      </form>
    </section>
  );
};

const LegalPage: React.FC<Props> = ({ kind, onLogoClick, onNavigate, isAdmin = false }) => {
  const content = pageContent[kind];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Header onLogoClick={onLogoClick} onNavigate={onNavigate} isAdmin={isAdmin} />
      <main className="mx-auto mt-8 max-w-4xl rounded-lg bg-white px-6 py-8 shadow-sm md:px-10">
        <div className="border-b border-gray-200 pb-6">
          <p className="text-xs font-semibold text-gray-400">最終更新日: {updatedAt}</p>
          <h1 className="mt-3 text-3xl font-bold text-gray-900">{content.title}</h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">{content.lead}</p>
        </div>

        <div className="mt-8 space-y-8">
          {content.sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-lg font-bold text-gray-900">{section.title}</h2>
              <div className="mt-3 space-y-3 text-sm leading-7 text-gray-600">
                {section.body.map((paragraph) =>
                  paragraph.includes('@') ? (
                    <p key={paragraph}>
                      <a className="font-semibold text-blue-600 hover:underline" href={`mailto:${paragraph}`}>
                        {paragraph}
                      </a>
                    </p>
                  ) : (
                    <p key={paragraph}>{paragraph}</p>
                  )
                )}
              </div>
            </section>
          ))}
        </div>

        {kind === 'contact' && <ContactForm />}
      </main>
      <Footer />
    </div>
  );
};

export default LegalPage;
